import { City } from './types/filters.types';
import { apiUrls } from './ApiUrls';
import { BaseApiService } from './BaseApiService';

type StateOption = { label: string; value: string; parentValue?: string };
type DropdownOption = { label: string; value: string; parentValue?: string };

/** Dedupes concurrent/StrictMode duplicate fetches on app startup. */
let statesPromise: Promise<StateOption[]> | null = null;
const masterDataPromises = new Map<string, Promise<unknown[]>>();

/**
 * Service for loading filter/master data from backend MasterDataController
 */
export class FiltersService extends BaseApiService {
  /** Get all active states */
  static async getStates(): Promise<StateOption[]> {
    if (!statesPromise) {
      statesPromise = this.fetchStatesOnce().catch((err) => {
        statesPromise = null;
        throw err;
      });
    }
    return statesPromise;
  }

  private static async fetchStatesOnce(): Promise<StateOption[]> {
    const response = await this.get(apiUrls.dropdowns.states, 'Get States');
    if (response.status === 'success' && response.data) {
      console.log('🔍 Raw states API response:', response.data);
      const mappedStates = response.data.map((item: {value: string, code: string, parentCode?: string}) => ({
        label: item.value,
        value: item.code,
        parentValue: item.parentCode
      }));
      console.log('🗺️ Mapped states:', mappedStates);
      return mappedStates;
    }
    throw new Error(response.message || 'Failed to fetch states');
  }

  /** Get all cities for a given state */
  static async getCitiesByState(state: string): Promise<{label: string, value: string, parentValue?: string}[]> {
    const response = await this.get(apiUrls.dropdowns.citiesbystate.replace('{stateCode}', state), 'Get Cities by State Code');
    if (response.status === 'success' && response.data) {
      return response.data.map((item: {label: string, value: string, code: string, parentCode?: string}) => ({
        label: item.value,  // City name for display
        value: item.code,   // City code for identification
        parentValue: item.parentCode
      }));
    }
    throw new Error(response.message || 'Failed to fetch cities');
  }

  /** Get all cities for a given state code */
  static async getCitiesByStateCode(stateCode: string): Promise<City[]> {
    const response = await this.get(apiUrls.dropdowns.citiesbystatecode.replace('{stateCode}', stateCode), 'Get Cities by State Code');
    if (response.status === 'success' && response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch cities');
  }

  static async getMasterData(masterdata: string): Promise<any> {
    return this.getMasterDataCached(masterdata);
  }

  static async getMasterDataByParent(masterdata: string, parentcode: string): Promise<any> {
    return this.getMasterDataCached(masterdata, parentcode);
  }

  private static getMasterDataCached(masterdata: string, parentcode?: string): Promise<unknown[]> {
    const key = parentcode ? `${masterdata}|${parentcode}` : masterdata;
    let promise = masterDataPromises.get(key);
    if (!promise) {
      promise = this.fetchMasterDataOnce(masterdata, parentcode).catch((err) => {
        masterDataPromises.delete(key);
        throw err;
      });
      masterDataPromises.set(key, promise);
    }
    return promise;
  }

  private static async fetchMasterDataOnce(masterdata: string, parentcode?: string): Promise<unknown[]> {
    const response = parentcode
      ? await this.get(
          apiUrls.dropdowns.masterdatabyparent
            .replace('{masterdata}', masterdata)
            .replace('{parentcode}', parentcode),
          'Get Master Data by Parent',
        )
      : await this.get(
          apiUrls.dropdowns.masterdata.replace('{masterdata}', masterdata),
          'Get Master Data',
        );
    if (response.status === 'success' && response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch master data');
  }

  /** Get categories from dictionary API */
  static async getDropdownItems(dropdownType: string, parentId?: string): Promise<DropdownOption[]> {
    try {
      let categoryItems: any[] = [];
      if(parentId) {
        categoryItems = await this.getMasterDataByParent(dropdownType, parentId);
      } else {
        categoryItems = await this.getMasterData(dropdownType);
      }
      return categoryItems.map(item => ({
        label: item.value,
        value: item.code,
        parentValue: item.parentCode
      }));
    } catch (error) {
      console.warn('Failed to load categories from dictionary API:', error);
      return [];
    }
  }
}

export default FiltersService; 